import { useState, useMemo } from "react";
import { useSearch, Link } from "wouter";
import {
  getGetFiscalYearBudgetPreviewQueryKey,
  useGetFiscalYearBudgetPreview,
} from "@workspace/api-client-react";
import type { BudgetPreviewLine } from "@workspace/api-client-react";
import { 
  ArrowLeft, Search, Loader2, AlertCircle, RefreshCw, 
  ChevronRight, CheckCircle2 
} from "lucide-react";
import { useMicroappContext } from "@/framework/microapp-context";
import { useWorkspaceCompany } from "@/hooks/use-workspace-company";

function formatMoney(value: number | null | undefined, currency = "USD") {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatMoneyShort(value: number | null | undefined, currency = "USD") {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function BudgetPreviewPage() {
  const { companyId } = useWorkspaceCompany();
  const { embedded } = useMicroappContext();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const sourceId = params.get("sourceId") || "";
  const fiscalYear = Number(params.get("fy") || "2026");

  const { data, isLoading, isError, error, refetch } = useGetFiscalYearBudgetPreview(
    { companyId, fiscalYear, sourceId },
    {
      query: {
        enabled: !!sourceId,
        queryKey: getGetFiscalYearBudgetPreviewQueryKey({
          companyId,
          fiscalYear,
          sourceId,
        }),
      },
    }
  );


  const [viewMode, setViewMode] = useState<"hierarchy" | "flat" | "source">("hierarchy");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDept, setSelectedDept] = useState("all");
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // Compute hierarchical data
  const processedData = useMemo(() => {
    if (!data?.lines) return { tree: [], flat: [], depts: [] };
    
    let filtered = data.lines;
    
    // Filter by department
    if (selectedDept !== "all") {
      filtered = filtered.filter(l => l.departmentCode === selectedDept);
    }
    
    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(l => 
        l.departmentName.toLowerCase().includes(q) ||
        l.departmentCode.toLowerCase().includes(q) ||
        l.glAccountName.toLowerCase().includes(q) ||
        l.glAccount.toLowerCase().includes(q) ||
        l.vendorName.toLowerCase().includes(q) ||
        l.spendItem.toLowerCase().includes(q) ||
        l.lineage.fileName.toLowerCase().includes(q)
      );
    }

    const depts = Array.from(new Set(data.lines.map(l => l.departmentCode))).map(code => {
      const name = data.lines.find(l => l.departmentCode === code)?.departmentName || code;
      return { code, name };
    }).sort((a, b) => a.name.localeCompare(b.name));

    // Build hierarchy
    const treeMap = new Map<string, any>();
    
    filtered.forEach(line => {
      const deptKey = `dept_${line.departmentCode}`;
      if (!treeMap.has(deptKey)) {
        treeMap.set(deptKey, {
          id: deptKey,
          type: "department",
          title: line.departmentName,
          subtitle: `DEPARTMENT ${line.departmentCode}`,
          annualAmount: 0,
          months: Object.fromEntries(MONTHS.map(m => [m, 0])),
          lines: 0,
          children: new Map<string, any>()
        });
      }
      const deptNode = treeMap.get(deptKey);
      deptNode.annualAmount += line.annualAmount;
      deptNode.lines += 1;
      MONTHS.forEach(m => deptNode.months[m] += (line.months[m] || 0));

      const glKey = `gl_${line.departmentCode}_${line.glAccount}`;
      if (!deptNode.children.has(glKey)) {
        deptNode.children.set(glKey, {
          id: glKey,
          type: "gl",
          title: line.glAccountName,
          subtitle: `GL ${line.glAccount}`,
          annualAmount: 0,
          months: Object.fromEntries(MONTHS.map(m => [m, 0])),
          lines: 0,
          children: []
        });
      }
      const glNode = deptNode.children.get(glKey);
      glNode.annualAmount += line.annualAmount;
      glNode.lines += 1;
      MONTHS.forEach(m => glNode.months[m] += (line.months[m] || 0));

      glNode.children.push({
        id: line.lineId,
        type: "line",
        data: line
      });
    });

    // Convert map to array and sort
    const tree = Array.from(treeMap.values()).map(dept => {
      dept.children = Array.from(dept.children.values()).map((gl: any) => {
        gl.children.sort((a: any, b: any) => b.data.annualAmount - a.data.annualAmount);
        return gl;
      }).sort((a: any, b: any) => b.annualAmount - a.annualAmount);
      return dept;
    }).sort((a, b) => b.annualAmount - a.annualAmount);

    return { tree, flat: filtered, depts };
  }, [data, selectedDept, searchQuery]);

  const toggleNode = (id: string) => {
    setExpandedNodes(prev => ({ ...prev, [id]: !prev[id] }));
    setPage(1);
  };

  const expandAll = () => {
    const next: Record<string, boolean> = {};
    processedData.tree.forEach(dept => {
      next[dept.id] = true;
      dept.children.forEach((gl: any) => {
        next[gl.id] = true;
      });
    });
    setExpandedNodes(next);
    setPage(1);
  };

  const collapseAll = () => {
    setExpandedNodes({});
    setPage(1);
  };

  const resetView = () => {
    setSearchQuery("");
    setSelectedDept("all");
    setPageSize(10);
    setPage(1);
    collapseAll();
  };

  // Flatten the tree for rendering if hierarchy, taking into account expanded state
  const renderRows = useMemo(() => {
    if (viewMode !== "hierarchy") {
      // Just paginate flat data
      const start = (page - 1) * pageSize;
      return processedData.flat.slice(start, start + pageSize).map((line, i) => ({
        ...line, _index: start + i + 1, _type: viewMode
      }));
    }

    // Hierarchy flattening logic
    let flattened: any[] = [];
    processedData.tree.forEach(dept => {
      flattened.push({ ...dept, _level: 0 });
      if (expandedNodes[dept.id]) {
        dept.children.forEach((gl: any) => {
          flattened.push({ ...gl, _level: 1 });
          if (expandedNodes[gl.id]) {
            gl.children.forEach((line: any) => {
              flattened.push({ ...line, _level: 2 });
            });
          }
        });
      }
    });

    const start = (page - 1) * pageSize;
    return flattened.slice(start, start + pageSize);
  }, [viewMode, processedData, expandedNodes, page, pageSize]);

  const totalRenderRows = useMemo(() => {
    if (viewMode !== "hierarchy") return processedData.flat.length;
    let count = 0;
    processedData.tree.forEach(dept => {
      count++;
      if (expandedNodes[dept.id]) {
        dept.children.forEach((gl: any) => {
          count++;
          if (expandedNodes[gl.id]) {
            count += gl.children.length;
          }
        });
      }
    });
    return count;
  }, [viewMode, processedData, expandedNodes]);

  const totalPages = Math.ceil(totalRenderRows / pageSize) || 1;

  if (!sourceId) {
    return (
      <div className="min-h-[100dvh] flex flex-col p-8 bg-[#fdfdfc] text-[#1a2f2b]">
        <div className="max-w-2xl mx-auto mt-20 text-center">
          <AlertCircle size={32} className="mx-auto mb-4 text-[#8d4b39]" />
          <h2 className="text-xl font-bold mb-2">No Budget Source Selected</h2>
          <p className="text-[#58736d] mb-6">A sourceId is required to preview the budget.</p>
          <Link href="/fiscal-year" className="tl-btn primary">Return to Fiscal Year</Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#fdfdfc] text-[#1a2f2b]">
        <div className="flex flex-col items-center gap-3 text-[#58736d]">
          <Loader2 className="animate-spin" size={24} />
          <span className="text-sm font-mono tracking-wide uppercase">Loading budget preview...</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-[100dvh] p-8 bg-[#fdfdfc] text-[#1a2f2b]">
        <div className="max-w-2xl mx-auto mt-20 p-6 bg-[#fff5f2] border border-[#e5b1a1] rounded-2xl">
          <div className="flex items-start gap-4">
            <AlertCircle className="text-[#aa5d45] shrink-0" />
            <div>
              <h3 className="font-bold text-[#8d4b39] mb-1">Failed to load budget preview</h3>
              <p className="text-[#9a6556] text-sm mb-4">{error.message}</p>
              <div className="flex gap-3">
                <button className="tl-btn primary" onClick={() => refetch()}><RefreshCw size={13} /> Retry</button>
                <Link href="/fiscal-year" className="tl-btn">Return</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-[100dvh] p-8 bg-[#fdfdfc] text-[#1a2f2b]">
        <div className="max-w-2xl mx-auto mt-20 p-6 bg-[#fffaf0] border border-[#ede0c8] rounded-2xl">
          <AlertCircle className="text-[#876739] mb-3" />
          <h3 className="font-bold text-[#715a39] mb-1">Budget preview is unavailable</h3>
          <p className="text-[#8a7658] text-sm mb-4">The published revision returned no preview data.</p>
          <Link href="/fiscal-year" className="tl-btn">Return to Fiscal Year</Link>
        </div>
      </div>
    );
  }

  const isTiedOut = Math.abs(data.difference) < 0.005;

  return (
    <div className={embedded ? "budget-preview-embedded" : "tl-shell"} style={{ display: 'block' }}>
      {!embedded && <header className="px-8 py-5 border-b flex items-start justify-between bg-[hsl(var(--background))] sticky top-0 z-20" style={{ borderColor: 'hsl(var(--border))' }}>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Link href="/fiscal-year" className="text-[#647c75] hover:text-[#235e56] transition-colors" title="Back to Fiscal Year" data-testid="link-back-to-fy">
              <ArrowLeft size={16} />
            </Link>
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#71857e]">Fiscal Year / Published Budget</span>
          </div>
          <h1 className="text-[28px] font-extrabold tracking-tight leading-tight m-0 text-[#12302a]">{data.budgetName}</h1>
          <p className="text-[12px] text-[#5b736c] m-0 mt-1">Inspect the exact read-only hierarchy, monthly distribution, and lineage of this immutable published revision.</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-bold" style={isTiedOut ? { backgroundColor: '#e7f1e9', borderColor: '#c8e2d4', color: '#1c6655' } : { backgroundColor: '#f8e9e4', borderColor: '#f0c8bc', color: '#933d26' }}>
          {isTiedOut ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
          {isTiedOut ? "Spread tied out" : "Imbalanced"}
        </div>
      </header>}

      <main className="px-8 py-6 max-w-[1600px] w-full mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-6">
          <div className="card" style={{ padding: '14px 16px' }}>
            <span className="block text-[10px] font-mono text-[#7b8a84] uppercase tracking-wider mb-1">Source File</span>
            <strong className="block text-[13px] font-bold truncate" title={data.sourceFile}>{data.sourceFile}</strong>
          </div>
          <div className="card" style={{ padding: '14px 16px' }}>
            <span className="block text-[10px] font-mono text-[#7b8a84] uppercase tracking-wider mb-1">FY</span>
            <strong className="block text-[13px] font-bold">FY{data.fiscalYear}</strong>
          </div>
          <div className="card" style={{ padding: '14px 16px' }}>
            <span className="block text-[10px] font-mono text-[#7b8a84] uppercase tracking-wider mb-1">Publication</span>
            <strong className="block text-[13px] font-bold capitalize">{data.status} · v{data.version}</strong>
            <span className="block text-[9px] font-mono text-[#7b8a84] mt-1">{new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(new Date(data.publishedAt))}</span>
          </div>
          <div className="card" style={{ padding: '14px 16px' }}>
            <span className="block text-[10px] font-mono text-[#7b8a84] uppercase tracking-wider mb-1">Normalized Lines</span>
            <strong className="block text-[13px] font-bold">{data.rowCount.toLocaleString()} lines</strong>
          </div>
          <div className="card" style={{ padding: '14px 16px' }}>
            <span className="block text-[10px] font-mono text-[#7b8a84] uppercase tracking-wider mb-1">Source Total</span>
            <strong className="block text-[13px] font-bold">{formatMoney(data.sourceTotal, data.currency)}</strong>
          </div>
          <div className="card" style={{ padding: '14px 16px' }}>
            <span className="block text-[10px] font-mono text-[#7b8a84] uppercase tracking-wider mb-1">Distributed Total</span>
            <strong className="block text-[13px] font-bold">{formatMoney(data.distributedTotal, data.currency)}</strong>
          </div>
          <div className="card" style={{ padding: '14px 16px', ...(isTiedOut ? { backgroundColor: '#f4f9f6' } : { backgroundColor: '#fff5f2', borderColor: '#f2d4cb' }) }}>
            <span className="block text-[10px] font-mono text-[#7b8a84] uppercase tracking-wider mb-1">Difference</span>
            <strong className={`block text-[13px] font-bold ${!isTiedOut ? 'text-[#aa3e21]' : 'text-[#2a7a67]'}`}>{formatMoney(data.difference, data.currency)}</strong>
          </div>
          <div className="card" style={{ padding: '14px 16px' }}>
            <span className="block text-[10px] font-mono text-[#7b8a84] uppercase tracking-wider mb-1">Source Revision</span>
            <strong className="block text-[12px] font-mono font-bold truncate" title={data.sourceId}>{data.sourceId}</strong>
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Controls Bar */}
          <div className="p-4 border-b flex flex-wrap items-center justify-between gap-4" style={{ borderColor: 'hsl(var(--border))', backgroundColor: '#fbfaf6' }}>
            <div className="flex items-center gap-4">
              <div className="method-row" style={{ gap: '4px', backgroundColor: '#f2efe7', padding: '4px', borderRadius: '10px', border: '1px solid #e7e3d8' }}>
                <button 
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-[6px] transition-colors ${viewMode === "hierarchy" ? "bg-white shadow-sm text-[#1b3d36]" : "text-[#6c7d78] hover:text-[#1b3d36]"}`}
                  style={viewMode === "hierarchy" ? { backgroundColor: 'white', color: '#1b3d36', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' } : { background: 'transparent' }}
                  onClick={() => { setViewMode("hierarchy"); setPage(1); }}
                  data-testid="tab-hierarchy"
                >
                  Hierarchy
                </button>
                <button 
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-[6px] transition-colors ${viewMode === "flat" ? "bg-white shadow-sm text-[#1b3d36]" : "text-[#6c7d78] hover:text-[#1b3d36]"}`}
                  style={viewMode === "flat" ? { backgroundColor: 'white', color: '#1b3d36', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' } : { background: 'transparent' }}
                  onClick={() => { setViewMode("flat"); setPage(1); }}
                  data-testid="tab-flat"
                >
                  Flat Rows
                </button>
                <button 
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-[6px] transition-colors ${viewMode === "source" ? "bg-white shadow-sm text-[#1b3d36]" : "text-[#6c7d78] hover:text-[#1b3d36]"}`}
                  style={viewMode === "source" ? { backgroundColor: 'white', color: '#1b3d36', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' } : { background: 'transparent' }}
                  onClick={() => { setViewMode("source"); setPage(1); }}
                  data-testid="tab-source"
                >
                  Source
                </button>
              </div>

              <div className="h-6 w-px bg-[#e4e1d7]" />
              <select 
                value={selectedDept} 
                onChange={e => { setSelectedDept(e.target.value); setPage(1); }}
                style={{ background: 'transparent', border: 'none', fontSize: '11px', fontWeight: 'bold', color: '#35524b', cursor: 'pointer', outline: 'none' }}
                data-testid="select-department"
                aria-label="Filter by department"
              >
                <option value="all">All Departments</option>
                {processedData.depts.map(d => (
                  <option key={d.code} value={d.code}>{d.name}</option>
                ))}
              </select>
              {viewMode === "hierarchy" && (
                <>
                  <button onClick={collapseAll} className="text-[11px] font-bold text-[#627a74] hover:text-[#214f47]">Collapse All</button>
                  <button onClick={expandAll} className="text-[11px] font-bold text-[#627a74] hover:text-[#214f47]">Expand All</button>
                </>
              )}
            </div>

            <div className="flex items-center gap-4">
              <button onClick={resetView} className="flex items-center gap-1.5 text-[11px] font-bold text-[#6a807a] hover:text-[#1a3832]">
                <RefreshCw size={11} /> Reset View
              </button>
              <div style={{ position: "relative" }}>
                <Search size={12} style={{ position: "absolute", left: 9, top: 10, color: "#84918b" }} />
                <input 
                  type="text" 
                  placeholder="Search hierarchy & lines..." 
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                  style={{ paddingLeft: 28, width: 220, background: 'white' }}
                  data-testid="input-search"
                />
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-[#697d78] font-mono uppercase">
                ROWS
                <select 
                  value={pageSize} 
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  style={{ background: 'transparent', fontWeight: 'bold', border: 'none', borderBottom: '1px solid #c8ccca', borderRadius: 0, padding: '0 0 2px 0', outline: 'none', color: '#23423a', cursor: 'pointer', minWidth: '40px' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <span className="tag" style={{ fontSize: '9px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Budget-wide</span>
            </div>
          </div>

          {/* Table Area */}
          <div className="overflow-x-auto min-h-[300px]" style={{ margin: 0 }}>
            <table className="w-full text-left border-collapse min-w-[1100px]" style={{ fontSize: '11px' }}>
              <thead>
                <tr>
                  {viewMode === "hierarchy" && (
                    <>
                      <th className="py-2.5 px-4 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider sticky left-0 bg-[#fcfbf9] z-10 w-[300px]">Hierarchy</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider text-right w-12">Lines</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider text-right w-28">Annual Budget</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider w-24">Spread</th>
                      {MONTHS.map(m => (
                        <th key={m} className="py-2.5 px-2 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider text-right">{m}</th>
                      ))}
                    </>
                  )}
                  {viewMode === "flat" && (
                    <>
                      <th className="py-2.5 px-4 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">Department</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">GL Account</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">Spend Item</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">Vendor</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider text-right">Annual Budget</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">Spread</th>
                      {MONTHS.map(m => (
                        <th key={m} className="py-2.5 px-2 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider text-right">{m}</th>
                      ))}
                    </>
                  )}
                  {viewMode === "source" && (
                    <>
                      <th className="py-2.5 px-4 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">Source File</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">Worksheet</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider text-right">Row #</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">Line ID</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">Department Code</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider">GL Account</th>
                      <th className="py-2.5 px-3 font-mono text-[9px] text-[#869691] uppercase font-semibold tracking-wider text-right">Annual Budget</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {renderRows.length === 0 ? (
                  <tr>
                    <td colSpan={100} className="py-12 text-center text-[12px] text-[#71857e]">
                      No rows match the current filters.
                    </td>
                  </tr>
                ) : (
                  renderRows.map((row, idx) => {
                    if (viewMode === "hierarchy") {
                      if (row._level === 0 || row._level === 1) {
                        const isExpanded = expandedNodes[row.id];
                        return (
                          <tr key={`${row.id}-${idx}`} className="border-b border-[#f2efeb] hover:bg-[#fbfbf9] group">
                            <td className="py-2.5 px-4 sticky left-0 bg-white group-hover:bg-[#fbfbf9] z-10 w-[300px]" style={{ paddingLeft: `${row._level * 20 + 16}px` }}>
                              <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => toggleNode(row.id)} data-testid={`toggle-node-${row.id}`}>
                                <ChevronRight size={14} className={`text-[#a3afaa] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                <div className="min-w-0">
                                  <div className="font-bold text-[12px] text-[#1b3d36] truncate">{row.title}</div>
                                  <div className="font-mono text-[9px] text-[#81928c] uppercase tracking-wider truncate">{row.subtitle}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-[11px] text-right text-[#576b66]">{row.lines}</td>
                            <td className="py-2 px-3 text-[12px] text-right font-mono font-medium text-[#294a42]">{formatMoney(row.annualAmount, data?.currency)}</td>
                            <td className="py-2 px-3 text-[10px] text-[#7b8f89] font-mono uppercase tracking-wider">Mixed</td>
                            {MONTHS.map(m => (
                              <td key={m} className="py-2 px-2 text-[11px] font-mono text-right text-[#697d78]">{formatMoneyShort(row.months[m], data?.currency)}</td>
                            ))}
                          </tr>
                        );
                      } else {
                        // Leaf line
                        const line = row.data as BudgetPreviewLine;
                        return (
                          <tr key={line.lineId} className="border-b border-[#f2efeb] hover:bg-[#fbfbf9] group bg-[#fdfdfc]">
                            <td className="py-2.5 px-4 sticky left-0 bg-[#fdfdfc] group-hover:bg-[#fbfbf9] z-10 w-[300px]" style={{ paddingLeft: `${row._level * 20 + 16}px` }}>
                              <div className="pl-5 min-w-0">
                                <div className="font-medium text-[11px] text-[#33534a] truncate">{line.spendItem || line.vendorName || "Unnamed Item"}</div>
                                <div className="font-mono text-[9px] text-[#899c96] truncate">{line.vendorName ? `Vendor: ${line.vendorName}` : `Item: ${line.spendItem}`}</div>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-[11px] text-right text-[#a1b0ab]">—</td>
                            <td className="py-2 px-3 text-[11px] text-right font-mono text-[#44625a]">{formatMoney(line.annualAmount, data?.currency)}</td>
                            <td className="py-2 px-3 text-[10px] text-[#7b8f89] font-mono uppercase tracking-wider">{line.spreadMethod}</td>
                            {MONTHS.map(m => (
                              <td key={m} className="py-2 px-2 text-[11px] font-mono text-right text-[#697d78]">{formatMoneyShort(line.months[m], data?.currency)}</td>
                            ))}
                          </tr>
                        );
                      }
                    } else if (viewMode === "flat") {
                      const line = row as BudgetPreviewLine;
                      return (
                        <tr key={line.lineId} className="border-b border-[#f2efeb] hover:bg-[#fbfbf9]">
                          <td className="py-2.5 px-4 text-[11px] text-[#1b3d36] font-medium">{line.departmentName}</td>
                          <td className="py-2.5 px-3 text-[11px] text-[#1b3d36]">{line.glAccountName}</td>
                          <td className="py-2.5 px-3 text-[11px] text-[#33534a]">{line.spendItem || "—"}</td>
                          <td className="py-2.5 px-3 text-[11px] text-[#33534a]">{line.vendorName || "—"}</td>
                          <td className="py-2.5 px-3 text-[11px] font-mono text-right font-medium text-[#294a42]">{formatMoney(line.annualAmount, data?.currency)}</td>
                          <td className="py-2.5 px-3 text-[10px] font-mono text-[#7b8f89] uppercase tracking-wider">{line.spreadMethod}</td>
                          {MONTHS.map(m => (
                            <td key={m} className="py-2.5 px-2 text-[11px] font-mono text-right text-[#697d78]">{formatMoneyShort(line.months[m], data?.currency)}</td>
                          ))}
                        </tr>
                      );
                    } else if (viewMode === "source") {
                      const line = row as BudgetPreviewLine;
                      return (
                        <tr key={line.lineId} className="border-b border-[#f2efeb] hover:bg-[#fbfbf9]">
                          <td className="py-2.5 px-4 text-[11px] text-[#1b3d36] font-medium max-w-[200px] truncate" title={line.lineage.fileName}>{line.lineage.fileName}</td>
                          <td className="py-2.5 px-3 text-[11px] text-[#33534a]">{line.lineage.worksheet || "—"}</td>
                          <td className="py-2.5 px-3 text-[11px] font-mono text-right text-[#576b66]">{line.lineage.sourceRowNumber ?? "—"}</td>
                          <td className="py-2.5 px-3 text-[10px] font-mono text-[#7b8f89]">{line.lineId}</td>
                          <td className="py-2.5 px-3 text-[11px] font-mono text-[#33534a]">{line.departmentCode}</td>
                          <td className="py-2.5 px-3 text-[11px] font-mono text-[#33534a]">{line.glAccount}</td>
                          <td className="py-2.5 px-3 text-[11px] font-mono text-right font-medium text-[#294a42]">{formatMoney(line.annualAmount, data?.currency)}</td>
                        </tr>
                      );
                    }
                    return null;
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="p-3 border-t border-[#ece9e1] flex items-center justify-between bg-[#fbfaf6]">
            <div className="text-[10px] font-mono text-[#7d908a] uppercase tracking-wider">
              Rows {Math.min((page - 1) * pageSize + 1, totalRenderRows)}-{Math.min(page * pageSize, totalRenderRows)} of {totalRenderRows}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-[10px] text-[#697d78] font-mono">
                PAGE
                <span className="font-bold text-[#23423a]">{page} / {totalPages}</span>
              </div>
              <div className="flex items-center gap-1">
                <button 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1 rounded text-[#5d736d] hover:bg-[#e4ece9] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={14} className="rotate-180" />
                </button>
                <button 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1 rounded text-[#5d736d] hover:bg-[#e4ece9] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 text-[10px] text-[#869691] font-mono px-2 max-w-2xl">
          Budget-wide row limit includes {viewMode === "hierarchy" ? "hierarchy rows" : "flat data rows"}. Source details reflect the exact spreadsheet cells mapped during normalization.
        </div>
      </main>
    </div>
  );
}
